import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { api } from '../utils/api';
import toast from 'react-hot-toast';

const LocationNode = ({ loc, depth, openAdd, openEdit, handleDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = loc.children && loc.children.length > 0;

  return (
    <div style={{ marginLeft: depth > 0 ? 20 : 0 }}>
      <div className="card" style={{ marginBottom: '8px', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: depth === 0 ? 'var(--brand-light)' : 'var(--surface)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {hasChildren ? (
            <button 
              onClick={() => setExpanded(!expanded)} 
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px', width: '20px', textAlign: 'center', padding: 0, color: 'var(--text2)' }}
            >
              {expanded ? '▼' : '▶'}
            </button>
          ) : <span style={{ width: '20px' }}></span>}
          <span style={{ fontWeight: 600 }}>{loc.name}</span>
          <span className="chip chip-gray" style={{ fontSize: '10px' }}>{loc.type}</span>
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          {loc.type !== 'AREA' && (
            <button className="btn btn-xs btn-primary" onClick={() => openAdd(loc.id, loc.type === 'COUNTRY' ? 'DIVISION' : loc.type === 'DIVISION' ? 'DISTRICT' : 'AREA')}>
              + Sub
            </button>
          )}
          <button className="btn btn-xs btn-outline" onClick={() => openEdit(loc)}>Edit</button>
          <button className="btn btn-xs btn-danger" onClick={() => handleDelete(loc.id)}>✕</button>
        </div>
      </div>
      {expanded && hasChildren && (
        <div className="stagger" style={{ marginTop: '4px' }}>
          {loc.children.map(child => (
            <LocationNode 
              key={child.id} 
              loc={child} 
              depth={depth + 1} 
              openAdd={openAdd} 
              openEdit={openEdit} 
              handleDelete={handleDelete} 
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default function LocationManager() {
  const { t } = useApp();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', type: 'COUNTRY', parent_id: null });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/locations?tree=1');
      setLocations(res.locations ?? []);
    } catch { toast.error('Failed to load locations'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/locations/${editing.id}`, form);
        toast.success('Location updated');
      } else {
        await api.post('/locations', form);
        toast.success('Location created');
      }
      setShowModal(false);
      load();
    } catch (err) { toast.error(err.error || 'Failed to save'); }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this location and all its sub-locations?')) return;
    try {
      await api.delete(`/locations/${id}`);
      toast.success('Deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  const openAdd = (parentId = null, type = 'COUNTRY') => {
    setEditing(null);
    setForm({ name: '', type, parent_id: parentId });
    setShowModal(true);
  };

  const openEdit = loc => {
    setEditing(loc);
    setForm({ name: loc.name, type: loc.type, parent_id: loc.parent_id });
    setShowModal(true);
  };



  return (
    <div className="container" style={{ paddingTop: '36px', paddingBottom: '60px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 700 }}>Location Management</h1>
        <button className="btn btn-primary btn-sm" onClick={() => openAdd()}>+ Add Country</button>
      </div>

      {loading ? <div className="spinner" /> : (
        <div className="stagger">
          {locations.length === 0 ? <p>No locations configured.</p> : locations.map(l => (
            <LocationNode 
              key={l.id} 
              loc={l} 
              depth={0} 
              openAdd={openAdd} 
              openEdit={openEdit} 
              handleDelete={handleDelete} 
            />
          ))}
        </div>
      )}

      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <form className="card" onSubmit={handleSubmit} style={{ width: '400px' }}>
            <h3 style={{ marginBottom: '16px' }}>{editing ? 'Edit Location' : 'Add Location'}</h3>
            <div className="form-group">
              <label className="form-label">Name</label>
              <input className="form-input" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Type</label>
              <select className="form-input" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                <option value="COUNTRY">Country</option>
                <option value="DIVISION">Division/State</option>
                <option value="DISTRICT">District/City</option>
                <option value="AREA">Area/Location</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button type="button" className="btn btn-outline btn-full" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary btn-full">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
